import React from 'react';
import { Button, notification, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { ItemType } from 'antd/lib/menu/hooks/useItems';

import { MainLayout } from 'components/Layout/MainLayout';
import { useQuery } from 'hooks/useQuery';
import { useAppDispatch, useAppSelector } from 'store';
import { PageTitles } from 'constants/pageTitles';
import { useDeepEffect } from 'utils/useDeepEffect';
import {
  deleteInvestmentSolution,
  loadInvestmentSolutionsList,
  setUnPublishInvestmentSolution,
} from 'store/slices/investmentSolutions/actions';
import { StatusesEnum } from 'constants/status';
import {
  InvestmentSolutionsItem,
  QueryFormValues,
} from 'store/slices/investmentSolutions/interfaces';
import { canBeUnPublished } from 'utils/rights';
import { StickyConfirmModal } from 'components/modals/StickyConfirmModal';
import { LangEnum } from 'constants/lang';

import { Filters } from './components/Filters';
import { Table } from './components/Table';

export const List = () => {
  const query = useQuery();
  const [isRemoveModalIsOpen, setRemoveModalIsOpen] =
    React.useState<boolean>(false);
  const [currentItem, setCurrentItem] = React.useState(null);

  const [sort, setSort] = React.useState<string | undefined>('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { b2bUrl } = useAppSelector(state => state.configSlice);
  const { list, total, limit } = useAppSelector(
    state => state.investmentSolutions
  );

  const loadProjects = () =>
    dispatch(
      loadInvestmentSolutionsList({ ...(query as QueryFormValues), sort })
    );

  const onCreateClick = () => navigate('add');

  const handleUnPublish = async (id: number) => {
    await dispatch(setUnPublishInvestmentSolution(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно снята с публикации',
        });
        loadProjects();
      });
  };

  useDeepEffect(() => {
    loadProjects();
  }, [query, sort]);

  const buttonList = [
    <Button key={1} onClick={onCreateClick} type='primary'>
      Создать
    </Button>,
  ];

  const onRemoveModalClose = () => {
    setRemoveModalIsOpen(false);
    setCurrentItem(null);
  };

  const onRemoveClick = async () => {
    await dispatch(deleteInvestmentSolution({ id: currentItem.id }))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно удалена',
        });
        loadProjects();
        onRemoveModalClose();
      });
  };

  const getActions = (item: InvestmentSolutionsItem): ItemType[] => [
    {
      key: 'status',
      label: (
        <a
          target='_blank'
          rel='noreferrer nofollow'
          href={`${b2bUrl}/${item.lang}/complete-solutions/${item.id}`}
        >
          На сайте
        </a>
      ),
      disabled: item.status !== StatusesEnum.PUBLISHED,
    },
    {
      key: 'edit',
      label: (
        <Link to={`/investmentSolutions/${item.id}/edit`}>Редактировать</Link>
      ),
    },
    item.lang === LangEnum.ru && {
      key: 'add-translation',
      label: (
        <Link to={`/investmentSolutions/${item.id}/add-translation`}>
          Создать копию для перевода
        </Link>
      ),
    },
    {
      key: 'unPublish',
      label: <Typography.Text>Снять с публикации</Typography.Text>,
      onClick: () => handleUnPublish(item?.id || 0),
      disabled: !canBeUnPublished(item),
    },
    {
      key: 'remove',
      label: 'Удалить',
      danger: true,
      onClick: () => {
        if (item.status === StatusesEnum.PUBLISHED) {
          notification.error({
            message: 'Необходимо снять сущность с публикации',
          });
        } else {
          setRemoveModalIsOpen(true);
          setCurrentItem(item);
        }
      },
    },
  ];

  return (
    <MainLayout
      pageTitle={PageTitles.investmentSolutions}
      extraButtonsList={buttonList}
      aside={<Filters />}
    >
      <Table
        items={list}
        setSort={setSort}
        total={total}
        limit={limit}
        getActions={getActions}
      />
      <StickyConfirmModal
        isOpen={isRemoveModalIsOpen}
        onRequestClose={onRemoveModalClose}
        title='Удаление сущности'
        okText='Удалить'
        text='Вы действительно хотите удалить эту cущность?'
        onSubmit={onRemoveClick}
        okButtonProps={{ danger: true }}
      />
    </MainLayout>
  );
};
